import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography
} from '@material-ui/core';
import { setCurrentJobPostId } from 'redux/slices/productsReducer';
import { getCampaignsThunk } from 'redux/slices/campaignsReducer';
import { TestAssessmentButton } from '@components/TestAssessmentButton';
import { getCandidateInfo } from 'redux/slices/candidate';
import { OpenJobPostCard } from 'components/CardJobPost';
import DashboardNavbar from 'layouts/dashboard/navbar/DashboardNavbar';
import { ArrowBackIosNew } from '@material-ui/icons';
import { getButtons } from 'constants/text/buttons';
import { OverviewSelect } from 'components/OverviewFilter';
import { getInitialJobPostData } from 'utils/helpers';
import { postDataByUrl } from 'requests';
import AIModal from 'components/AIModal';
import { htmlToText } from 'html-to-text';
import CultureProfileCharts, {
  ChartDescription
} from '../../components/CultureProfileCharts';
import { MindgageScoresTooltip } from '../../components/MindgageScoresTooltip';
import SvgIconStyle from '../../components/SvgIconStyle';
import { Title1 } from '../../components/text/title';
import {
  getTranslatedList,
  MAX_COMPETENCY_SELECTED_LENGTH,
  MIN_COMPETENCY_SELECTED_LENGTH,
  MIN_COMPETENCY_SLIDER_RANGE,
  SALARY_CHOICES_INR
} from '../../constants/constants';
import { HOST_URL, PATH_DASHBOARD, PATH_HOME } from '../../routes/paths';
import Page from '../../components/containers/Page';
import { updateJobPost } from '../../requests/vacancy';
import useAuth from '../../hooks/useAuth';
import LoadingScreen from '../../components/LoadingScreen';
import StepperComponent from '../../components/Stepper';
import CompetenciesSelect from '../../components/CompetenciesSelect';
import {
  fieldValidate,
  formValidate,
  makeNotRequired
} from '../../helpers/formValidation';

import { DynamicForm } from '../../components/DynamicForm';
import { EditButton } from '../../components/Buttons';
import { getComparedData } from '../../helpers/listValues';
import { RootState } from '../../redux/store';
import { ApplyJobButton } from '../../components/ApplyJobButton';
import PageContainer from '../../components/containers/PageContainer';
import { getCardValuesByType } from '../../helpers/cardValues';
import { SharedButtons } from '../../components/SharedButtons';
import { useFormFieldsByType } from '../../helpers/formFields';
import useLocales from '../../hooks/useLocales';
import ScoreField from '../../components/ScoreField';
import {
  TypographyDescriptionJobPost,
  TypographyTitleJobPost
} from '../../styled';
import CultureProfileSecondStep from './CultureProfileSecondStep';
import { JobPostCompetenciesType, JobPostType } from './types';
import {
  fetchAllJobPosts,
  fetchJob,
  patchJobPost,
  postJob,
  fetchRemoteJobs,
  resetNewJobPost
} from '../../redux/slices/jobSlice';
import { fetchMindgageMetrics } from '../../redux/slices/assessment';
import MindgageMetricsStep from './MindgageMetricsStep';

interface Competency {
  name: string;
  id: number;
  description: string;
}

export const initialCurrentJobPost = {
  id: null,
  user: null,
  title: '',
  description: '',
  customer: '',
  status: '',
  studies: [],
  contract: 0,
  degree: 0,
  work_exp: 0,
  salary: null,
  hourly_rate: null,
  locations: [],
  industries: [],
  skills: [],
  musts: [],
  perks: [],
  available: null,
  drivers_license: false,
  anonymous: false,
  assessment_required: false
};

const initialCurrentCompetencies = {
  competency_labels: [],
  competency_min_scores: [],
  competency_ids: [],
  descriptions: []
};

const EditJobPost = ({ options, isNew, userId, jobPostId, setEdit }: any) => {
  const { DEFAULT_MESSAGE_TEXTS } = getTranslatedList();
  const { t, currentLang } = useLocales();

  const { company }: any = useSelector(
    (state: RootState) => state.generalRecruiter
  );

  const isFreeAccount = company?.if_free_trial;
  const enableMindgage = company?.enable_mindgage === true;
  const assessmentProviderEnabled = company?.assessment_provider !== 0;

  if (company.currency === 'INR') {
    options.salaries = SALARY_CHOICES_INR;
  }

  const [isLoading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState<any>({});
  const [existingJobPost, setExistingJobPost] = useState<JobPostType>(
    initialCurrentJobPost
  );
  const [currentJobPost, setCurrentJobPost] = useState<JobPostType>(
    initialCurrentJobPost
  );

  const [openAIPopUp, setOpenAIPopUp] = useState(false);

  const [competenciesReceived, setCompetenciesReceived] = useState(false);

  const [aiAvatar, setAiAvatar] = useState('/static/img/illustrations/rocketship.svg');

  const [aiTitle, setAiTitle] = useState('');

  const [aiMessage, setAiMessage] = useState('');

  const [proceedNext, setProceedNext] = useState(false);

  const [aiOperationType, setAiOperationType] = useState<
    'job-description' | 'competencies' | null
  >(null);

  const [currentCompetencies, setCurrentCompetencies] =
    useState<JobPostCompetenciesType>(initialCurrentCompetencies);

  const [mindgageMetrics, setMindgageMetrics] = useState<any[]>([]);
  const [mindgageScores, setMindgageScores] = useState<number[]>([]);

  // Add streaming abort controller for job description generation
  const streamingAbortControllerRef = useRef<AbortController | null>(null);

  const dispatch = useDispatch();
  const { newJobPost, jobData, dataFromJobFile }: any = useSelector(
    (state: RootState) => state.job
  );
  const { mindgageMetrics: mindgageMetricsFromStore } = useSelector(
    (state: RootState) => state.assessment
  );
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const is_freelancer =
    currentJobPost.contract === 4 || existingJobPost.contract === 4;

  useEffect(() => {
    if (jobData.id && !isNew) updateInitialData(jobData);
  }, [jobData, isNew]);

  useEffect(() => {
    if (dataFromJobFile) {
      handleSetJobPostData(
        'description',
        dataFromJobFile.Description || dataFromJobFile.description
      );
      handleSetJobPostData(
        'title',
        dataFromJobFile.Title || dataFromJobFile.title
      );

      if (dataFromJobFile.Competencies || dataFromJobFile.competencies) {
        const list: Competency[] = filterCompetenciesById(
          dataFromJobFile.Competencies || dataFromJobFile.competencies
        );

        const newCompetencies = {
          competency_labels: list.map((item) => item.name),
          competency_ids: list.map((item) => item.id),
          competency_min_scores: new Array(list.length).fill(
            MIN_COMPETENCY_SLIDER_RANGE
          ),
          descriptions: list.map((item) => item.description || '')
        };

        setCurrentCompetencies(newCompetencies);
      }
    }
  }, [dataFromJobFile]);

  useEffect(() => {
    if (jobData?.mindgage_metrics && Array.isArray(jobData.mindgage_metrics)) {
      const scores = jobData.mindgage_metrics.map((m: any) => m.min_score || 1);
      setMindgageScores(scores);
      // Fetch metrics to populate the form
      if (enableMindgage) {
        dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
      }
    }
  }, [jobData, enableMindgage, dispatch, currentLang]);

  const updateInitialData = (data: any) => {
    setLoading(true);
    const { curJobPost, curCompetencies } = getInitialJobPostData(data, isNew);
    setCurrentJobPost(curJobPost);
    setCurrentCompetencies(curCompetencies);
    setTimeout(() => setLoading(false), 100);
  };

  const filterCompetenciesById = (targetIds: number[]) => {
    const allCompetencies = options.competencies?.flatMap(
      (group: any) => group.competencies
    );

    const filteredCompetencies = targetIds.reduce((acc, id) => {
      const matchedNames = allCompetencies.filter(
        (competency: Competency) => competency.id === id
      );

      return acc.concat(matchedNames);
    }, []);

    return filteredCompetencies;
  };

  const handleSetJobPostData = (field: string, value: any) => {
    let updatedValue = value;

    if (field === 'skills') {
      updatedValue = value.map((item: any) => {
        if (typeof item === 'string') {
          return { id: item, name: item };
        }
        return item;
      });
    }

    // This might need to be updated
    const updatedJobPost = {
      ...currentJobPost,
      [field]: updatedValue
    };

    if (field === 'salary' && updatedValue != null) {
      updatedJobPost.hourly_rate = null;
    } else if (field === 'hourly_rate' && updatedValue != null) {
      updatedJobPost.salary = null;
    }

    setCurrentJobPost(updatedJobPost);

    const fieldIsEmpty = fieldValidate(updatedValue);
    if (fieldIsEmpty) {
      setErrors({
        ...errors,
        [field]: true
      });
    } else if (!fieldIsEmpty && errors[field]) {
      const errorUpdate = { ...errors };
      delete errorUpdate[field];
      setErrors(errorUpdate);
    }
  };

  const errorHandler = (errors: any) => {
    setErrors(errors);
    if (Object.keys(errors).length) {
      enqueueSnackbar(DEFAULT_MESSAGE_TEXTS.required, {
        variant: 'warning'
      });
    }
  };

  const setDataFromExistingPost = (data: any, isRemote: boolean) => {
    if (isRemote && !isNew) {
      setCurrentJobPost({ ...currentJobPost, remote_job: data });
    } else if (!isRemote && !isNew) {
      setCurrentJobPost(data);
      setExistingJobPost(data);
      updateInitialData(data);
      formValidate(data, errorHandler);
    } else if (!isRemote && isNew) {
      // Exclude culture_profile when creating new job from existing one
      // because it may only contain mindgage metrics and should be created fresh
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { culture_profile: _culture_profile, ...dataWithoutCultureProfile } = data;
      const result = {
        ...dataWithoutCultureProfile,
        ...(existingJobPost.title && { title: existingJobPost.title }),
        ...(existingJobPost.status && { status: existingJobPost.status }),
        ...(existingJobPost.description && {
          description: existingJobPost.description
        }),
        ...(existingJobPost.contract && {
          contract: existingJobPost.contract
        }),
        ...(existingJobPost.remote_job && {
          remote_job: existingJobPost.remote_job
        }),
        ...(is_freelancer
          ? { hourly_rate: data.hourly_rate, salary: null }
          : { salary: data.salary, hourly_rate: null })
      };
      setCurrentJobPost(result);
      setExistingJobPost(result);
      updateInitialData(result);
      formValidate(result, errorHandler);
    } else {
      // Exclude culture_profile when creating new job from existing one
      // because it may only contain mindgage metrics and should be created fresh
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        culture_profile: _dataCultureProfile,
        ...dataWithoutCultureProfile
      } = data;
      const result = {
        ...existingJobPost,
        ...dataWithoutCultureProfile,
        id: existingJobPost.id || null,
        remote_job: data,
        ...(is_freelancer
          ? { hourly_rate: data.hourly_rate, salary: null }
          : { salary: data.salary, hourly_rate: null })
      };
      setCurrentJobPost(result);
      setExistingJobPost(result);
      updateInitialData(result);
      formValidate(result, errorHandler);
    }
  };

  const handleSetCompetencies = (label: string, id: number, text: string) => {
    const {
      competency_labels,
      competency_ids,
      competency_min_scores,
      descriptions
    } = currentCompetencies;
    const index = competency_labels.indexOf(label);

    if (index !== -1) {
      const newSelected = [...competency_labels];
      newSelected.splice(index, 1);

      setCurrentCompetencies({
        competency_labels: newSelected,
        competency_ids: [
          ...competency_ids.slice(0, index),
          ...competency_ids.slice(index + 1)
        ],
        competency_min_scores: [
          ...competency_min_scores.slice(0, index),
          ...competency_min_scores.slice(index + 1)
        ],
        descriptions: [
          ...descriptions.slice(0, index),
          ...descriptions.slice(index + 1)
        ]
      });
    } else if (competency_labels.length < MAX_COMPETENCY_SELECTED_LENGTH) {
      setCurrentCompetencies({
        competency_labels: [...competency_labels, label],
        competency_ids: [...competency_ids, id],
        competency_min_scores: [
          ...competency_min_scores,
          MIN_COMPETENCY_SLIDER_RANGE
        ],
        descriptions: [...descriptions, text]
      });
    }
  };

  const handleSetCompetenciesScore = (i: number, values: number[] | number) => {
    const { competency_min_scores } = currentCompetencies;
    const value: number = typeof values === 'number' ? values : values[0];

    const newMinRanges = [...competency_min_scores];
    newMinRanges[i] = value;

    setCurrentCompetencies({
      ...currentCompetencies,
      competency_min_scores: newMinRanges
    });
  };

  const handleSubmit = async () => {
    const { competency_ids, competency_min_scores } = currentCompetencies;

    const competencies = competency_ids.map((el: number, i: number) => ({
      competency: el,
      min_score: competency_min_scores[i]
    }));

    const data: any = {
      ...currentJobPost,
      ...(assessmentProviderEnabled && {
        culture_profile: currentJobPost?.culture_profile?.id
      }),
      studies: currentJobPost.studies.map((study: any) => study?.id || study),
      industries: currentJobPost.industries.map(
        (industry: any) => industry?.id || industry
      ),
      skills: currentJobPost.skills.map((el: any) => el.id),
      ...(assessmentProviderEnabled && { competencies }),
      customer: currentJobPost?.customer?.id,
      remote_job: currentJobPost?.remote_job?.id?.toString() || null
    };

    // Add mindgage_metrics if enabled and scores are set
    if (
      enableMindgage &&
      mindgageMetrics.length > 0 &&
      mindgageScores.length > 0
    ) {
      data.mindgage_metrics = mindgageMetrics.map(
        (metric: any, index: number) => ({
          metric_id: metric.id,
          min_score: mindgageScores[index] || 1
        })
      );
    }

    if (isNew) {
      data.user = userId;
      delete data.id;
      dispatch(postJob(data));
      setEdit(false);
    } else {
      const id = jobPostId;
      await dispatch(patchJobPost({ id, data }));
      await dispatch(fetchJob(id));
      setEdit(false);
      navigate(`${PATH_DASHBOARD.jobPosts.getJobPostUrl(id)}`);
    }
  };

  useEffect(() => {
    if (newJobPost && newJobPost.id)
      navigate(`${PATH_DASHBOARD.jobPosts.getJobPostUrl(newJobPost.id)}`);
  }, [newJobPost]);

  const getCompetenciesFromAI = async () => {
    setAiAvatar('/static/img/illustrations/rocketship.svg');
    setAiTitle(t('The Magic is Working...'));
    setAiMessage(t("We are extracting competencies from the job information. This may take up to 1 minute. If you don't wish to wait, please click 'Skip' to select the competencies manually."));
    setAiOperationType('competencies');
    setOpenAIPopUp(true);
    setCompetenciesReceived(true);

    try {
      const res = await postDataByUrl(
        '/api/openai/extract_competencies/',
        {
          job_title: currentJobPost.title,
          job_description: currentJobPost.description
        },
        null,
        false,
        10000
      );

      if (
        (res.competencies && res.competencies.length) ||
        (res.Competencies && res.Competencies.length)
      ) {
        const list: Competency[] = filterCompetenciesById(
          res.competencies || res.Competencies
        );

        const newCompetencies = {
          competency_labels: list.map((item) => item.name),
          competency_ids: list.map((item) => item.id),
          competency_min_scores: new Array(list.length).fill(
            MIN_COMPETENCY_SLIDER_RANGE
          ),
          descriptions: list.map((item) => item.description || '')
        };
        setAiAvatar('/static/img/illustrations/lightbulb.svg');
        setCurrentCompetencies(newCompetencies);
        setOpenAIPopUp(false);
        setAiOperationType(null);
        setActiveStep(activeStep + 1);
        setAiTitle('');
        setAiMessage('');
      } else {
        enqueueSnackbar(t('NORA is not able to suggest any competency at the moment, please go ahead and select your desired competencies manually.'), {
            variant: 'error'
          }
        );
        setOpenAIPopUp(false);
        setAiOperationType(null);
      }
    } catch (error) {
      console.error(error);
      setOpenAIPopUp(false);
      setAiOperationType(null);
    }
  };

  const checkForCompetencies = async () => {
    if (!assessmentProviderEnabled) {
      return;
    }
    await getCompetenciesFromAI();
  };

  useEffect(() => {
    if (activeStep === 1) {
      setProceedNext(false);
      setCompetenciesReceived(false);
    }
  }, [activeStep]);

  // Fetch mindgage metrics when step is reached and mindgage is enabled
  useEffect(() => {
    if (enableMindgage) {
      const mindgageStepIndex = assessmentProviderEnabled ? 4 : 2; // Step index depends on whether competencies steps are shown
      if (activeStep === mindgageStepIndex) {
        if (mindgageMetricsFromStore && mindgageMetricsFromStore.length > 0) {
          // Use metrics from Redux store
          setMindgageMetrics(mindgageMetricsFromStore);
          // Initialize scores if not already set
          if (mindgageScores.length === 0) {
            setMindgageScores(
              new Array(mindgageMetricsFromStore.length).fill(1)
            );
          }
        } else {
          // Fetch metrics if not in store
          dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
        }
      }
    }
  }, [
    activeStep,
    enableMindgage,
    assessmentProviderEnabled,
    mindgageMetricsFromStore,
    mindgageScores.length,
    dispatch,
    currentLang
  ]);

  // Update local metrics when Redux store updates
  useEffect(() => {
    if (
      mindgageMetricsFromStore &&
      mindgageMetricsFromStore.length > 0 &&
      mindgageMetrics.length === 0
    ) {
      setMindgageMetrics(mindgageMetricsFromStore);
      if (mindgageScores.length === 0) {
        setMindgageScores(new Array(mindgageMetricsFromStore.length).fill(1));
      }
    }
  }, [mindgageMetricsFromStore, mindgageMetrics.length, mindgageScores.length]);

  const handleSetMindgageScores = (index: number, score: number) => {
    const newScores = [...mindgageScores];
    newScores[index] = score;
    setMindgageScores(newScores);
  };

  // Cleanup streaming abort controller on unmount
  useEffect(
    () => () => {
      if (streamingAbortControllerRef.current) {
        streamingAbortControllerRef.current.abort();
      }
    },
    []
  );

  // Handle skip button for job description/competencies (has next steps)
  const handleSkipAI = () => {
    if (streamingAbortControllerRef.current) {
      streamingAbortControllerRef.current.abort();
      streamingAbortControllerRef.current = null;
    }
    setOpenAIPopUp(false);
    setAiOperationType(null);
    if (aiOperationType === 'competencies') {
      setActiveStep(activeStep + 1);
    }
  };

  // Handle abort button for job description streaming
  const handleAbortJobDescription = () => {
    if (streamingAbortControllerRef.current) {
      streamingAbortControllerRef.current.abort();
      streamingAbortControllerRef.current = null;
    }
    setOpenAIPopUp(false);
    setAiOperationType(null);
    setAiAvatar('/static/img/illustrations/rocketship.svg');
  };

  // Create Skip button for job description/competencies
  const createSkipButton = () => (
    <Button
      variant="outlined"
      onClick={handleSkipAI}
      style={{
        marginTop: '16px',
        borderColor: '#ddd',
        color: '#666'
      }}
    >
      {t('Skip')}
    </Button>
  );

  const generateJobDescriptionStreaming = async () => {
    try {
      setAiAvatar('/static/img/illustrations/rocketship.svg');
      setAiMessage(
        t('NORA is generating a description for you, please be patient.')
      );
      setAiOperationType('job-description');
      setOpenAIPopUp(true);

      // Clear any existing description
      setCurrentJobPost((prev) => ({
        ...prev,
        description: ''
      }));

      // Create abort controller for this streaming request
      streamingAbortControllerRef.current = new AbortController();

      const response = await fetch('/api/openai/generate_description/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          job_title: currentJobPost.title
        }),
        signal: streamingAbortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if we can actually stream this response
      if (!response.body) {
        const text = await response.text();
        setCurrentJobPost((prev) => ({
          ...prev,
          description: text
        }));
        setOpenAIPopUp(false);
        return;
      }

      setOpenAIPopUp(false);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedDescription = '';

      // Process streaming response recursively
      const processStream = async () => {
        try {
          const { done, value } = await reader.read();

          if (done) {
            return;
          }

          const chunk = decoder.decode(value, { stream: true });
          accumulatedDescription += chunk;
          setCurrentJobPost((prev) => ({
            ...prev,
            description: accumulatedDescription
          }));

          await processStream();
        } catch (error: any) {
          if (error.name === 'AbortError') {
            return;
          }
          console.error('Error in processStream:', error);
          throw error;
        }
      };

      await processStream();
      setOpenAIPopUp(false);
      setAiOperationType(null);
      setAiAvatar('/static/img/illustrations/lightbulb.svg');
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setOpenAIPopUp(false);
        setAiOperationType(null);
        setAiAvatar('/static/img/illustrations/rocketship.svg');
        return;
      }
      console.error('Error in streaming job description generation:', error);
      enqueueSnackbar(error?.message || DEFAULT_MESSAGE_TEXTS.error, {
        variant: 'error'
      });
      setOpenAIPopUp(false);
      setAiOperationType(null);
      setAiAvatar('/static/img/illustrations/rocketship.svg');
    }
  };

  const generateJobDescription = async () => {
    await generateJobDescriptionStreaming();
  };

  const firstFormFields = {
    studies: currentJobPost.studies,
    title: currentJobPost.title,
    contract: currentJobPost.contract,
    degree: currentJobPost.degree,
    work_exp: currentJobPost.work_exp,
    ...(is_freelancer
      ? { hourly_rate: currentJobPost.hourly_rate }
      : { salary: currentJobPost.salary }),
    locations: currentJobPost.locations,
    industries: currentJobPost.industries,
    available: currentJobPost.available,
    drivers_license: currentJobPost.drivers_license,
    assessment_required: currentJobPost.assessment_required,
    ...(assessmentProviderEnabled && {
      culture_profile: currentJobPost.culture_profile
    }),
    ...(currentJobPost.remote_job && { remote_job: currentJobPost.remote_job })
  };

  const secondFormFields = {
    description: currentJobPost.description,
    skills: currentJobPost.skills,
    musts: currentJobPost.musts,
    perks: currentJobPost.perks,
    status: currentJobPost.status,
    anonymous: currentJobPost.anonymous
  };

  const firstFields = [
    {
      type: 'select',
      value: null,
      label: t('You can select one of the existing job posts to auto-fill the form and save yourself time'),
      onChange: (e: any, el: any) => setDataFromExistingPost(el, false),
      options: options.allJobPosts,
      helperText: t('You can select one of existing job-posts to auto fill form')
    },
    {
      isRemoteJob: true,
      type: 'select',
      options: options.remote_job,
      value: currentJobPost.remote_job || null,
      label: t('If you have the same job in your ATS system, please input the Job'),
      onChange: (e: any, el: any) => setDataFromExistingPost(el, true)
    },
    ...useFormFieldsByType(
      'job-post-first-step',
      currentJobPost,
      handleSetJobPostData,
      options,
      errors,
      '',
      '',
      company.currency,
      company.is_agency,
      undefined,
      is_freelancer,
      undefined,
      assessmentProviderEnabled
    )
  ];

  const secondFields = useFormFieldsByType(
    'job-post-second-step',
    currentJobPost,
    handleSetJobPostData,
    options,
    errors,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    generateJobDescription
  );

  const { competency_labels, competency_min_scores, descriptions } =
    currentCompetencies;

  const steps = [
    {
      label: t('Please fill out all necessary data'),
      nextEnable: true,
      subtitle: 'STEP 1',
      nextValidation: () =>
        formValidate(
          firstFormFields,
          errorHandler,
          makeNotRequired(firstFormFields, 'available')
        ),
      startSrc: '',
      activeSrc: '/static/img/jobs/icons/necessary-active.svg',
      endSrc: '/static/img/jobs/icons/necessary-end.svg'
    },
    {
      label: t('Select job requirements'),
      nextEnable: true,
      subtitle: 'STEP 2',
      nextValidation: () =>
        formValidate(
          secondFormFields,
          errorHandler,
          makeNotRequired(secondFormFields, 'skills')
        ),

      startSrc: '/static/img/jobs/icons/requirements-start.svg',
      activeSrc: '/static/img/jobs/icons/requirements-active.svg',
      endSrc:
        assessmentProviderEnabled || enableMindgage
          ? '/static/img/jobs/icons/requirements-end.svg'
          : ''
    },
    ...(assessmentProviderEnabled
      ? [
          {
            label: t('Select competencies'),
            subtitle: 'STEP 3',
            nextEnable:
              competency_labels.length >= MIN_COMPETENCY_SELECTED_LENGTH &&
              competency_labels.length <= MAX_COMPETENCY_SELECTED_LENGTH,

            startSrc: '/static/img/jobs/icons/competencies-start.svg',
            activeSrc: '/static/img/jobs/icons/competencies-active.svg',
            endSrc: '/static/img/jobs/icons/competencies-end.svg'
          },
          {
            label: t('Select competencies score'),
            subtitle: 'STEP 4',
            nextEnable: true,
            startSrc: '/static/img/jobs/icons/score-start.svg',
            activeSrc: '/static/img/jobs/icons/score-active.svg',
            endSrc: enableMindgage ? '/static/img/jobs/icons/score-end.svg' : ''
          }
        ]
      : []),
    ...(enableMindgage
      ? [
          {
            label: t('Select Mindgage Metric'),
            subtitle: assessmentProviderEnabled ? 'STEP 5' : 'STEP 3',
            nextEnable: true,
            startSrc: '/static/img/jobs/icons/score-start.svg',
            activeSrc: '/static/img/jobs/icons/score-active.svg',
            endSrc: ''
          }
        ]
      : [])
  ];

  const formKey = `form-${isNew}-${is_freelancer}`;

  const getActiveStepComponent = () => {
    // Calculate step indices based on assessment provider and mindgage settings
    const competenciesStepIndex = assessmentProviderEnabled ? 2 : -1;
    const competenciesScoreStepIndex = assessmentProviderEnabled ? 3 : -1;
    let mindgageStepIndex = -1;
    if (enableMindgage) {
      mindgageStepIndex = assessmentProviderEnabled ? 4 : 2;
    }

    switch (activeStep) {
      case 0:
        return (
          <DynamicForm
            key={formKey}
            inputValues={firstFields}
            title={t('Necessary Data')}
            isNew={isNew}
            styling={true}
          />
        );
      case 1:
        return (
          <DynamicForm
            inputValues={secondFields}
            type="job-post-second-step"
            generateJobDescription={generateJobDescription}
            currentJobPost={currentJobPost}
          />
        );
      case competenciesStepIndex:
        if (assessmentProviderEnabled) {
          return (
            <Box>
              <Typography>
              {t('We advise you to select no more than 10 competencies And you should only go for the once that really matter to achieve success in the work he candidate needs to execute The average is 7 competencies per matching profile But depending on your results you can always add or delete competencies This way you can fine-tune your ideal profile')}
              </Typography>
              <CompetenciesSelect
                competencies={options.competencies}
                selectedList={competency_labels}
                onSelect={handleSetCompetencies}
              />
            </Box>
          );
        }
        return 'No such step';
      case competenciesScoreStepIndex:
        if (assessmentProviderEnabled) {
          return (
            <CultureProfileSecondStep
              selectedOptionsLabels={competency_labels}
              minRanges={competency_min_scores}
              setRanges={handleSetCompetenciesScore}
              descriptions={descriptions}
            />
          );
        }
        return 'No such step';
      case mindgageStepIndex:
        if (enableMindgage) {
          return (
            <MindgageMetricsStep
              metrics={mindgageMetrics}
              scores={mindgageScores}
              setScores={handleSetMindgageScores}
            />
          );
        }
        return 'No such step';
      default:
        return 'No such step';
    }
  };

  const title = `${isNew ? t('Add') : t('Edit')} ${t('Job Post')}`;

  if (isLoading) return <LoadingScreen />;
  return (
    <Page title={title}>
      <PageContainer
        style={{
          padding: 0
        }}
      >
        <DashboardNavbar title={title} />
        <AIModal
          open={openAIPopUp}
          onClose={() => {
            if (aiOperationType === 'job-description') {
              handleAbortJobDescription();
            } else {
              setOpenAIPopUp(false);
            }
          }}
          customAvatar={aiAvatar}
          customTitle={aiTitle}
          customMessage={aiMessage}
          textStyle={{ fontSize: '16px', fontWeight: 400 }}
          customButton={
            aiOperationType === 'job-description' ||
            aiOperationType === 'competencies'
              ? createSkipButton()
              : undefined
          }
        />
        <Grid mt={2}>
          <StepperComponent
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            onFinish={handleSubmit}
            onBack={() => setEdit(false)}
            steps={steps}
            editJobPost={!isNew}
            isFreeAccount={isFreeAccount}
            checkForCompetencies={
              assessmentProviderEnabled
                ? () => checkForCompetencies()
                : () => {}
            }
            generateJobDescription={() => generateJobDescription()}
            competenciesReceived={
              competenciesReceived ||
              dataFromJobFile?.competencies?.length ||
              dataFromJobFile?.Competencies?.length
            }
            onNextStep={proceedNext}
            assessmentProviderEnabled={assessmentProviderEnabled}
          >
            {getActiveStepComponent()}
          </StepperComponent>
        </Grid>
      </PageContainer>
    </Page>
  );
};

const InfoJobPost = ({
  isNew,
  // isCandidate,
  setEdit,
  userId,
  jobPostId
}: any) => {
  const dispatch = useDispatch();
  const { jobData }: any = useSelector((state: RootState) => state.job);
  const navigate = useNavigate();

  const { mainInfo } = useSelector(
    (state: RootState) => state.candidate.candidate
  );
  const { count, isLoading } = useSelector(
    (state: RootState) => state.campaignsReducer
  );
  const { result: competencies }: any = useSelector(
    (state: RootState) => state.candidate.assessmentResult
  );
  const { t, currentLang } = useLocales();

  const {
    user,
    isAdmin,
    isCandidate,
    isEmployee,
    isRecruiterPlus,
    isRecruiter,
    isHiringManager
  } = useAuth();

  const { mindgageMetrics } = useSelector(
    (state: RootState) => state.assessment
  );

  const { company }: any = useSelector(
    (state: RootState) => state.generalRecruiter
  );

  const hasJobPostCompetencies =
    jobData?.competencies && jobData.competencies.length > 0;

  const [mindgageScoresTooltipIsOpen, setMindgageScoresTooltipIsOpen] =
    useState(false);

  const isCandidateOrEmployee = isCandidate || isEmployee;

  const isOwner = jobData.user === userId;

  const ownedByBoundingRecruiter = jobData.user === user.recruiter;

  const canTakeAction =
    isAdmin ||
    isRecruiterPlus ||
    (isRecruiter && isOwner) ||
    (isHiringManager && ownedByBoundingRecruiter);

  const canAccessCampaigns =
    isAdmin || isRecruiterPlus || isRecruiter || isHiringManager;

  const canApplyForJob =
    (isCandidateOrEmployee && !jobData.assessment_required) ||
    (isCandidateOrEmployee && competencies);

  const cantApplyForJobButCanTakeAssessment =
    isCandidateOrEmployee && jobData.assessment_required && !competencies;

  const canEditJob = !isNew && canTakeAction;

  const cultureCompetenciesDescription = useMemo(
    () =>
      jobData?.culture_profile?.competencies?.map(
        (el: any) => el?.competency?.description
      ),
    [jobData?.culture_profile?.competencies?.length]
  );

  const candidateCompetencies = mainInfo?.competencies || [];

  const matchCompared = getComparedData(
    candidateCompetencies,
    jobData?.competencies
  );

  const cultureCompared = getComparedData(
    candidateCompetencies,
    jobData?.culture_profile?.competencies
  );

  const [culture_labels, culture_min_scores] = useMemo(() => {
    if (!jobData?.culture_profile) {
      return [[], []];
    }

    const labels =
      jobData.culture_profile.competencies?.map(
        (el: any) => el?.competency?.name
      ) ?? [];
    const minScores =
      jobData.culture_profile.competencies?.map((el: any) => el?.min_score) ??
      [];

    return [labels, minScores];
  }, [jobData?.culture_profile]);

  const opt: any = useSelector((state: RootState) => state.fixturesReducer);
  const options = { ...opt };

  if (company?.currency === 'INR') {
    options.salaries = SALARY_CHOICES_INR;
  }

  // Fetch mindgage metrics if job has mindgage_metrics
  // This should work for both candidates and recruiters
  useEffect(() => {
    if (
      jobData?.mindgage_metrics &&
      jobData.mindgage_metrics.length > 0 &&
      (!mindgageMetrics || mindgageMetrics.length === 0)
    ) {
      dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
    }
  }, [jobData?.mindgage_metrics, mindgageMetrics, dispatch, currentLang]);

  // Get candidate mindgage result for comparison
  const candidateMindgageResult = useMemo(() => {
    // Try to get from mainInfo (for recruiters viewing candidate profiles)
    const mainInfoAny = mainInfo as any;
    const mindgageFromMainInfo = mainInfoAny?.mindgage?.result;
    // The structure is an array of {metric_id, score} objects
    if (mindgageFromMainInfo && Array.isArray(mindgageFromMainInfo)) {
      return mindgageFromMainInfo;
    }
    return null;
  }, [mainInfo]);

  // Process mindgage metrics data for chart
  // Show chart if job has mindgage_metrics, regardless of enableMindgage setting
  // (enableMindgage is for editing, but candidates should see the chart if job has metrics)
  const mindgageChartData = useMemo(() => {
    if (
      !jobData?.mindgage_metrics ||
      jobData.mindgage_metrics.length === 0 ||
      !mindgageMetrics ||
      mindgageMetrics.length === 0
    ) {
      return null;
    }

    // Create a map of metric_id -> min_score from job's mindgage_metrics
    const scoreMap = new Map<number, number>();
    jobData.mindgage_metrics.forEach((m: any) => {
      if (m.metric_id && m.min_score !== undefined) {
        scoreMap.set(m.metric_id, m.min_score);
      }
    });

    // Create a map of metric_id -> score from candidate's mindgage result
    const candidateScoreMap = new Map<number, number>();
    if (candidateMindgageResult && Array.isArray(candidateMindgageResult)) {
      candidateMindgageResult.forEach((m: any) => {
        if (m.metric_id && m.score !== undefined) {
          candidateScoreMap.set(m.metric_id, m.score);
        }
      });
    }

    // Map metrics to chart format
    const labels: string[] = [];
    const points: number[] = [];
    const comparePoints: (number | null)[] = [];
    const descriptions: any[] = [];

    mindgageMetrics.forEach((metric: any) => {
      if (metric.id && metric.name) {
        const score = scoreMap.get(metric.id);
        if (score !== undefined) {
          labels.push(metric.name);
          points.push(score);
          // Get candidate score for this metric if available
          const candidateScore = candidateScoreMap.get(metric.id);
          const compareValue =
            candidateScore !== undefined ? candidateScore : null;
          comparePoints.push(compareValue);
          descriptions.push({
            id: metric.id,
            name: metric.name,
            description: metric.description || ''
          });
        }
      }
    });

    return labels.length > 0
      ? { labels, points, comparePoints, descriptions }
      : null;
  }, [jobData?.mindgage_metrics, mindgageMetrics, candidateMindgageResult]);

  // Create sorted points for mindgage metrics (similar format to competencies)
  const sortedMindgagePoints = useMemo(() => {
    if (!mindgageChartData) {
      return [];
    }

    // Format mindgage data to match the structure expected by ChartDescription
    return mindgageChartData.labels
      .map((label: string, index: number) => {
        const description = mindgageChartData.descriptions[index];
        return {
          point: mindgageChartData.points[index] || 0,
          label,
          id: description?.id || index + 1,
          descriptions: description?.description || '',
          comparePoint:
            mindgageChartData.comparePoints[index] !== null &&
            mindgageChartData.comparePoints[index] !== undefined
              ? mindgageChartData.comparePoints[index]
              : null
        };
      })
      .sort((a: any, b: any) => (b.point || 0) - (a.point || 0));
  }, [mindgageChartData]);

  const fields = getCardValuesByType(
    'job-post',
    jobData,
    options,
    company.currency
  );

  const startCampaign = () => {
    navigate(`${PATH_DASHBOARD.campaigns.startCampaign}`);
    setCurrentJobPostId(jobData.id?.toString(), jobData.title);
  };

  const getCampaign = async () => {
    canAccessCampaigns &&
      userId &&
      jobData?.id &&
      dispatch(getCampaignsThunk(userId, undefined, undefined, jobData?.id));
  };

  useEffect(() => {
    getCampaign();
  }, [canAccessCampaigns, userId, jobData]);

  const openOrderedCampaigns = (jobPostId: number) => {
    navigate(
      `${PATH_DASHBOARD.campaigns.getCampaignsOverviewForJobPost(jobPostId)}`
    );
  };

  const { BACK } = getButtons();

  const [currentJobPost, setCurrentJobPost] = useState<JobPostType>(
    initialCurrentJobPost
  );

  const handleSetJobPostData = async (value: any) => {
    if (value) {
      const opption = STATUSES[value - 1];
      const current = {
        ...jobData,
        status: opption.label
      };
      setCurrentJobPost(current);
      await updateJobPost(jobData?.id, {
        status: opption.label
      });
    } else {
      setCurrentJobPost(currentJobPost);
    }
  };

  const { STATUSES } = getTranslatedList();

  const getStatusOption = (job: any) =>
    STATUSES.find((elem) => elem.label === job?.status);

  useEffect(() => {
    if (jobData?.id) {
      setCurrentJobPost(jobData);
    }
  }, [jobData]);

  const convertHtmlToMailtoFormat = (html: string) =>
    htmlToText(html, {
      wordwrap: 130,
      preserveNewlines: true
    });

  const InviteCandidate = (
    url: string,
    title: string,
    description: string
  ): void => {
    // const plainTextDescription = convertHtmlToMailtoFormat(description);

    // const subject = encodeURIComponent(title);
    const body = encodeURIComponent(url);
    const mailtoLink = `mailto:?body=${body}`;

    window.location.href = mailtoLink;
  };

  return (
    <Page title={jobData.title}>
      <PageContainer
        style={{
          padding: 0
        }}
      >
        <DashboardNavbar title={jobData.title} />
        <Button
          onClick={() => navigate('/jobs/')}
          variant="text"
          style={{
            color: '#562C82',
            marginBottom: '20px'
          }}
        >
          <ArrowBackIosNew
            style={{
              fontSize: '18px',
              margin: '0 0 5px 0'
            }}
          />
          {BACK}
        </Button>
        <Card sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              borderBottom: '1px solid #e0e0e0',
              p: 2
            }}
          >
            {canTakeAction && (
              <Box>
                {count > 0 && !isLoading && (
                  <Button
                    onClick={() => openOrderedCampaigns(jobData.id)}
                    variant="outlined"
                    color="success"
                    sx={{ mr: 2 }}
                  >
                    {t('Ordered campaigns')}: {count}
                  </Button>
                )}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: '10px'
                  }}
                >
                  <Button
                    onClick={startCampaign}
                    variant="contained"
                    style={{
                      background:
                        'linear-gradient(92.25deg, #9b5fda -2.57%, #562c82 113.38%)',
                      padding: '10px 45px',
                      borderRadius: '30px'
                    }}
                  >
                    {t('Start a new job marketing Campaign')}
                  </Button>
                </div>
              </Box>
            )}
            {canApplyForJob && (
              <ApplyJobButton
                jobPostId={jobData.id}
                onApply={() => {}}
                isApplied={!!jobData?.is_applied}
              />
            )}
            {cantApplyForJobButCanTakeAssessment && <TestAssessmentButton />}
            <Box />
            <Grid
              style={{
                marginLeft: 'auto',
                marginRight: '20px',
                display: 'flex'
              }}
            >
              {canTakeAction && (
                <OverviewSelect
                  width={180}
                  candidate={{ color: '' }}
                  options={STATUSES}
                  activeOption={getStatusOption(currentJobPost)?.value}
                  setOption={(value: any) => handleSetJobPostData(value)}
                  styleAutocomplete={{
                    borderRadius: '30px'
                  }}
                />
              )}
              {canEditJob && <EditButton onClick={setEdit} />}
            </Grid>
            <SharedButtons
              url={`${HOST_URL}${PATH_HOME.getJobUrl(jobData.id)}`}
              title={jobData.title}
              description={jobData.description}
            />
          </Box>
          <CardContent>
            {canTakeAction && (
              <Box sx={{ display: 'flex', justifyContent: 'end', mb: '12px' }}>
                <Button
                  onClick={() =>
                    InviteCandidate(
                      `${HOST_URL}${PATH_HOME.getJobUrl(jobData.id)}`,
                      jobData.title,
                      jobData.description
                    )
                  }
                  variant="contained"
                  style={{
                    background:
                      'linear-gradient(92.25deg, #9b5fda -2.57%, #562c82 113.38%)',
                    padding: '10px 45px',
                    borderRadius: '30px'
                  }}
                >
                  {t('Invite candidate for this job')}
                </Button>
              </Box>
            )}
            <Box
              color="GrayText"
              style={{
                whiteSpace: 'pre-wrap',
                fontWeight: 400,
                fontSize: '18px',
                color: '#131417'
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: jobData.description }} />
            </Box>
          </CardContent>
          <CardContent>
            <Grid container>
              {fields.map((el: any, i: number) => (
                <Grid item xs={4} key={i} sx={{ mt: 2 }}>
                  <TypographyTitleJobPost variant="h6">
                    {t(el.label)}
                  </TypographyTitleJobPost>
                  <TypographyDescriptionJobPost
                    variant="body1"
                    color="GrayText"
                  >
                    {t(el.value)}
                  </TypographyDescriptionJobPost>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
        <CardContent
          style={{
            padding: '20px 0'
          }}
        >
          <Grid
            container
            spacing={1}
            style={{
              justifyContent: 'space-between'
            }}
          >
            <Grid item xs={12} md={6} sx={{}}>
              {jobData?.score && (
                <ScoreField
                  type="talent"
                  score={jobData?.score}
                  variant="h5"
                  align="center"
                />
              )}
              <OpenJobPostCard
                item={jobData}
                onDelete={() => {}}
                onEdit={() => {}}
                editable={false}
                compared={matchCompared}
                fullWidth={true}
                showCultureScore={false}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              {jobData?.culture_score && (
                <ScoreField
                  type="culture"
                  score={jobData?.culture_score}
                  variant="h5"
                  align="center"
                />
              )}
              <OpenJobPostCard
                item={{
                  ...jobData,
                  competency_labels: culture_labels,
                  culture_score: jobData?.culture_score, // Use the actual culture_score value, not the array
                  descriptions: cultureCompetenciesDescription,
                  competencies: jobData?.culture_profile?.competencies,
                  title: jobData?.culture_profile?.name,
                  mindgage_metrics: undefined // Culture profile should not show cognitive metrics
                }}
                onDelete={() => {}}
                onEdit={() => {}}
                editable={false}
                compared={cultureCompared}
                nocustomer={true}
                fullWidth={true}
                showJobScore={false}
                showCultureScore={true} // Explicitly enable culture score display
              />
            </Grid>
          </Grid>
          {/* Cognitive Metrics Card - shown separately only when job has competencies (main graph shows competencies) */}
          {/* If job has no competencies, mindgage is already shown in the main OpenJobPostCard */}
          {mindgageChartData && hasJobPostCompetencies && (
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 2, mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      marginBottom: '16px'
                    }}
                  >
                    <SvgIconStyle
                      src="/static/img/profile/professional-skills.svg"
                      style={{ marginRight: '10px' }}
                    />
                    <Title1 padding={0}>{t('Mindgage')}</Title1>
                  </Box>
                  <CultureProfileCharts
                    title={t('Cognitive Metrics')}
                    isMindgage={true}
                    labels={mindgageChartData.labels}
                    points={mindgageChartData.points}
                    comparePoints={
                      mindgageChartData.comparePoints.some(
                        (p: any) => p !== null && p !== undefined
                      )
                        ? mindgageChartData.comparePoints
                        : undefined
                    }
                    compareTitle="candidate"
                    descriptions={mindgageChartData.descriptions}
                    profile={true}
                    showTooltips={false}
                    minScale={1}
                    maxScale={5}
                    mindgageScoresTooltipIsOpen={mindgageScoresTooltipIsOpen}
                    setMindgageScoresTooltipIsOpen={
                      setMindgageScoresTooltipIsOpen
                    }
                    showCompetencyComparison={mindgageChartData.comparePoints.some(
                      (p: any) => p !== null && p !== undefined
                    )}
                  />
                  <MindgageScoresTooltip
                    open={mindgageScoresTooltipIsOpen}
                    onClose={() => setMindgageScoresTooltipIsOpen(false)}
                  />
                </Card>
                <ChartDescription
                  labels={sortedMindgagePoints}
                  candidateCompetencies={[]}
                  showCompetencyComparison={
                    mindgageChartData?.comparePoints?.some(
                      (p: any) => p !== null && p !== undefined
                    ) || false
                  }
                  jobPostCompetencies={mindgageChartData?.descriptions || []}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </PageContainer>
    </Page>
  );
};

const JobPost = () => {
  const dispatch = useDispatch();
  const [isEdit, setEdit] = useState(false);
  const { loading, allJobPosts, competencies, remote_job }: any = useSelector(
    (state: RootState) => state.job
  );
  const { user, isAdmin, isCandidate, isEmployee, isRecruiterPlus } = useAuth();
  let { id } = useParams<any>();

  const location = useLocation();

  if (location.hash === '#new') {
    id = 'new';
  }

  const isNew = id === 'new';
  const isCandidateOrEmployee = isCandidate || isEmployee;
  const userId = user.id;

  const handleEdit = () => {
    setEdit(!isEdit);
  };

  const getInitialData = () => {
    if (isNew) {
      dispatch(resetNewJobPost());
    }
    dispatch(getCandidateInfo(user?.candidate_id));
    if (!isNew && id) {
      dispatch(fetchJob(id));
    } else setEdit(true);

    if (!isCandidateOrEmployee) {
      dispatch(fetchAllJobPosts());
    }
  };

  useEffect(() => {
    if (isAdmin || isRecruiterPlus) {
      dispatch(fetchRemoteJobs());
    }
  }, [isAdmin, isRecruiterPlus]);

  const optionsStore: any = useSelector(
    (state: RootState) => state.fixturesReducer
  );

  const optionsObj = {
    ...optionsStore,
    allJobPosts,
    competencies,
    remote_job
  };

  useEffect(() => {
    getInitialData();
    dispatch(resetNewJobPost());
  }, [id, isNew, isCandidateOrEmployee, userId, user]);

  if (loading && !isNew) return <LoadingScreen />;
  return isNew || isEdit ? (
    <EditJobPost
      options={optionsObj}
      isNew={isNew}
      jobPostId={id}
      userId={userId}
      setEdit={handleEdit}
    />
  ) : (
    <InfoJobPost
      isNew={isNew}
      jobPostId={id}
      userId={userId}
      setEdit={handleEdit}
    />
  );
};

export default JobPost;
